import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Download,
  MoreVertical,
  Pencil,
  Plus,
  Search,
  SlidersHorizontal,
  Trash2,
  Upload,
} from 'lucide-react'

import {
  formatDate,
  formatCPF,
  formatPaymentType,
  formatTableMoney,
  getInitials,
} from '../utils/formatters'

export default function PatientsPage({
  filteredPatients,
  isExporting = false,
  isImporting = false,
  isLoading,
  onDeletePatient,
  onEditPatient,
  onExportPatients,
  onFilterPatients,
  onImportPatients,
  onNewPatient,
  onPageChange,
  onToggleActionMenu,
  openActionPatientId,
  pagination,
  patientSearch,
  setPatientSearch,
}) {
  return (
    <div>
      <div className="mb-5 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-normal text-[#1A1A1A] lg:text-3xl">Pacientes</h1>
          <p className="mt-2 max-w-3xl text-sm font-medium leading-6 text-[#5A5147] sm:max-w-2xl lg:text-base">
            Gerencie registros de pacientes, histórico de procedimentos e detalhes financeiros em um só lugar.
          </p>
        </div>
        <button
          className="flex h-10 w-full items-center justify-center gap-2 rounded-full bg-[#1A1A1A] px-5 text-sm font-bold text-white shadow-[0_12px_24px_rgba(26,26,26,0.12)] transition hover:bg-[#2C2A28] sm:w-auto"
          type="button"
          onClick={onNewPatient}
        >
          <Plus size={18} strokeWidth={2.8} aria-hidden="true" />
          Novo Paciente
        </button>
      </div>

      <section className="mb-5 rounded-xl bg-white p-4 shadow-[0_18px_45px_rgba(26,26,26,0.05)]">
        <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
          <div className="flex flex-1 flex-col gap-3 md:flex-row">
            <div className="flex h-14 min-w-0 flex-1 items-center gap-3 rounded-lg border border-[#EEE8DF] bg-[#FBFAF8] px-4 text-[#5A5147] md:h-10 md:gap-3 md:rounded-md">
              <Search className="h-6 w-6 shrink-0 md:h-[18px] md:w-[18px]" strokeWidth={2.8} aria-hidden="true" />
              <input
                className="h-full min-w-0 flex-1 bg-transparent text-base font-semibold outline-none placeholder:text-[#5A5147] md:text-sm"
                placeholder="Buscar paciente..."
                value={patientSearch}
                onChange={(event) => setPatientSearch(event.target.value)}
              />
            </div>

            <button
              className="hidden h-10 items-center justify-center gap-2 rounded-full border border-[#D6CDBF] bg-white px-4 text-sm font-bold text-[#5A5147] md:flex"
              type="button"
              onClick={onFilterPatients}
            >
              <SlidersHorizontal size={18} strokeWidth={2.6} aria-hidden="true" />
              Filtrar
              <ChevronDown size={16} strokeWidth={2.6} aria-hidden="true" />
            </button>
          </div>

          <div className="grid min-w-0 grid-cols-2 gap-2 md:flex md:justify-end md:gap-3">
            <button
              className="col-span-2 flex h-10 min-w-0 items-center justify-center gap-1.5 rounded-full border border-[#D6CDBF] bg-white px-3 text-xs font-bold text-[#5A5147] md:hidden"
              type="button"
              onClick={onFilterPatients}
            >
              <SlidersHorizontal size={14} strokeWidth={2.6} aria-hidden="true" />
              Filtrar
            </button>
            <button
              className="flex h-10 min-w-0 items-center justify-center gap-1.5 rounded-full border border-[#D6CDBF] bg-white px-3 text-xs font-bold text-[#8A6819] shadow-sm transition hover:bg-[#FBF8F4] disabled:cursor-not-allowed disabled:opacity-60 md:h-10 md:gap-2 md:px-4 md:text-sm"
              disabled={isImporting}
              type="button"
              onClick={onImportPatients}
            >
              <Upload size={14} strokeWidth={2.8} aria-hidden="true" />
              {isImporting ? 'Importando...' : 'Importar'}
            </button>
            <button
              className="flex h-10 min-w-0 items-center justify-center gap-1.5 rounded-full border border-[#D6CDBF] bg-white px-3 text-xs font-bold text-[#8A6819] shadow-sm transition hover:bg-[#FBF8F4] disabled:cursor-not-allowed disabled:opacity-60 md:h-10 md:gap-2 md:px-4 md:text-sm"
              disabled={isExporting}
              type="button"
              onClick={onExportPatients}
            >
              <Download size={14} strokeWidth={2.8} aria-hidden="true" />
              {isExporting ? 'Exportando...' : 'Exportar'}
            </button>
          </div>
        </div>
      </section>

      <section className="overflow-hidden rounded-xl bg-white shadow-[0_18px_45px_rgba(26,26,26,0.05)]">
        <div className="divide-y divide-[#E5DED3] md:hidden">
          {filteredPatients.length ? (
            filteredPatients.map((patient, index) => (
              <PatientMobileCard
                key={patient.id}
                index={index}
                isActionOpen={openActionPatientId === patient.id}
                patient={patient}
                onDelete={onDeletePatient}
                onEdit={onEditPatient}
                onToggleActions={onToggleActionMenu}
              />
            ))
          ) : (
            <div className="px-4 py-8 text-center text-sm font-semibold text-[#5A5147]">
              {isLoading ? 'Carregando pacientes...' : 'Nenhum paciente cadastrado'}
            </div>
          )}
        </div>

        <div className="hidden overflow-x-auto md:block">
          <table className="w-full min-w-[820px] border-collapse text-left">
            <thead className="bg-[#FBFAF8] text-[#5A5147]">
              <tr>
                <TableHead>Paciente</TableHead>
                <TableHead>Procedimento</TableHead>
                <TableHead align="right">Valor<br />(R$)</TableHead>
                <TableHead align="center">Tempo<br />(min)</TableHead>
                <TableHead>Tipo</TableHead>
                <TableHead>CPF</TableHead>
                <TableHead>Data</TableHead>
                <TableHead align="center">Ações</TableHead>
              </tr>
            </thead>
            <tbody>
              {filteredPatients.length ? (
                filteredPatients.map((patient, index) => (
                  <tr
                    className={`border-b border-[#E5DED3] ${
                      index % 2 === 1 ? 'bg-[#FCFBF9]' : 'bg-white'
                    }`}
                    key={patient.id}
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <Avatar name={patient.name} index={index} />
                        <span className="text-sm font-bold text-[#1A1A1A]">{patient.name}</span>
                      </div>
                    </td>
                    <td className="max-w-[220px] px-4 py-3 text-sm font-semibold leading-6 text-[#5A5147]">
                      {patient.procedure}
                    </td>
                    <td className="px-4 py-3 text-right text-sm font-bold text-[#1A1A1A]">
                      {formatTableMoney(patient.value)}
                    </td>
                    <td className="px-4 py-3 text-center text-sm font-bold text-[#1A1A1A]">
                      {patient.duration_minutes}
                    </td>
                    <td className="px-4 py-3">
                      <PaymentBadge value={patient.payment_type} />
                    </td>
                    <td className="px-4 py-3 text-sm font-semibold text-[#5A5147]">
                      {patient.payment_type === 'particular' ? (formatCPF(patient.cpf) || '-') : '-'}
                    </td>
                    <td className="px-4 py-3 text-sm font-semibold text-[#5A5147]">
                      {formatDate(patient.created_at)}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <PatientActions
                        isOpen={openActionPatientId === patient.id}
                        patient={patient}
                        onDelete={onDeletePatient}
                        onEdit={onEditPatient}
                        onToggle={onToggleActionMenu}
                      />
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td className="px-4 py-8 text-center text-sm font-semibold text-[#5A5147]" colSpan="8">
                    {isLoading ? 'Carregando pacientes...' : 'Nenhum paciente cadastrado'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="flex flex-col gap-3 border-t border-[#E5DED3] px-4 py-4 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-sm font-semibold text-[#5A5147]">
            Mostrando {filteredPatients.length ? 1 : 0} a {filteredPatients.length} de{' '}
            {pagination.total} pacientes
          </p>
          <div className="flex items-center gap-2 text-sm font-bold text-[#5A5147] sm:gap-3">
            <button
              className="disabled:opacity-35"
              disabled={pagination.page <= 1 || isLoading}
              type="button"
              aria-label="Página anterior"
              onClick={() => onPageChange(pagination.page - 1)}
            >
              <ChevronLeft size={18} />
            </button>
            {getPaginationItems(pagination.page, Math.max(pagination.total_pages, 1)).map(
              (item, index) =>
                item === 'ellipsis' ? (
                  <span key={`ellipsis-${index}`} className="hidden px-0.5 sm:inline">
                    ...
                  </span>
                ) : (
                  <button
                    key={item}
                    className={`flex h-9 min-w-9 items-center justify-center rounded-full px-2 transition disabled:opacity-35 ${
                      item === pagination.page
                        ? 'bg-[#1A1A1A] text-white'
                        : 'text-[#5A5147] hover:bg-[#FBF8F4]'
                    }`}
                    disabled={isLoading || item === pagination.page}
                    type="button"
                    aria-current={item === pagination.page ? 'page' : undefined}
                    aria-label={`Página ${item}`}
                    onClick={() => onPageChange(item)}
                  >
                    {item}
                  </button>
                ),
            )}
            <button
              className="disabled:opacity-35"
              disabled={pagination.page >= pagination.total_pages || isLoading}
              type="button"
              aria-label="Próxima página"
              onClick={() => onPageChange(pagination.page + 1)}
            >
              <ChevronRight size={18} />
            </button>
          </div>
        </div>
      </section>
    </div>
  )
}

export function ActionMenuOverlay({ menu, onClose, onDelete, onEdit }) {
  return (
    <>
      <button
        className="fixed inset-0 z-30 cursor-default bg-transparent"
        type="button"
        aria-label="Fechar menu de ações"
        onClick={onClose}
      />
      <div
        className="fixed z-40 w-36 overflow-hidden rounded-lg border border-[#E5DED3] bg-white py-1 text-left shadow-[0_12px_30px_rgba(17,24,39,0.16)]"
        style={{ left: `${menu.left}px`, top: `${menu.top}px` }}
      >
        <button
          className="flex w-full items-center gap-2 px-3 py-2 text-sm font-semibold text-[#1A1A1A] transition hover:bg-[#F8E1E7]"
          type="button"
          onClick={() => onEdit(menu.patient)}
        >
          <Pencil size={15} />
          Editar
        </button>
        <button
          className="flex w-full items-center gap-2 px-3 py-2 text-sm font-semibold text-[#C91D1D] transition hover:bg-[#FFF1F1]"
          type="button"
          onClick={() => onDelete(menu.patient)}
        >
          <Trash2 size={15} />
          Deletar
        </button>
      </div>
    </>
  )
}

function PatientMobileCard({
  index,
  isActionOpen,
  onDelete,
  onEdit,
  onToggleActions,
  patient,
}) {
  return (
    <article className="grid gap-4 p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3">
          <Avatar name={patient.name} index={index} />
          <div className="min-w-0">
            <h3 className="truncate text-sm font-bold text-[#1A1A1A]">{patient.name}</h3>
            <p className="mt-0.5 truncate text-sm font-semibold text-[#5A5147]">
              {patient.procedure}
            </p>
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <PaymentBadge value={patient.payment_type} />
          <PatientActions
            isOpen={isActionOpen}
            patient={patient}
            onDelete={onDelete}
            onEdit={onEdit}
            onToggle={onToggleActions}
          />
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3 rounded-lg bg-[#FBFAF8] p-3">
        <MobileDatum label="Valor" value={`R$ ${formatTableMoney(patient.value)}`} />
        <MobileDatum label="Tempo" value={`${patient.duration_minutes} min`} />
        <MobileDatum label="Data" value={formatDate(patient.created_at)} />
      </div>

      {patient.payment_type === 'particular' && (
        <div className="flex items-center gap-2 text-sm font-semibold text-[#5A5147]">
          <span>CPF:</span>
          <span>{formatCPF(patient.cpf) || '-'}</span>
        </div>
      )}
    </article>
  )
}

function PatientActions({ isOpen, onToggle, patient }) {
  return (
    <div className="inline-flex justify-center">
      <button
        className={`flex h-8 w-8 items-center justify-center rounded-md text-[#5A5147] transition hover:bg-[#FBF8F4] ${
          isOpen ? 'bg-[#F8E1E7] text-[#5A5147]' : ''
        }`}
        type="button"
        aria-label="Ações do paciente"
        onClick={(event) => onToggle(patient, event)}
      >
        <MoreVertical size={18} />
      </button>
    </div>
  )
}

function MobileDatum({ label, value }) {
  return (
    <div className="min-w-0">
      <p className="text-[11px] font-bold uppercase text-[#5A5147]">{label}</p>
      <p className="mt-1 truncate text-xs font-bold text-[#1A1A1A]">{value}</p>
    </div>
  )
}

function getPaginationItems(current, total) {
  if (total <= 1) return [1]
  if (total <= 7) {
    return Array.from({ length: total }, (_, index) => index + 1)
  }

  const items = []
  const pushPage = (page) => {
    if (items[items.length - 1] !== page) items.push(page)
  }

  pushPage(1)
  if (current > 3) items.push('ellipsis')

  const start = Math.max(2, current - 1)
  const end = Math.min(total - 1, current + 1)
  for (let page = start; page <= end; page += 1) {
    pushPage(page)
  }

  if (current < total - 2) items.push('ellipsis')
  pushPage(total)

  return items
}

function TableHead({ align = 'left', children }) {
  const alignClass = align === 'right' ? 'text-right' : align === 'center' ? 'text-center' : ''

  return (
    <th className={`px-4 py-3 text-xs font-bold uppercase leading-4 ${alignClass}`}>
      {children}
    </th>
  )
}

function Avatar({ index, name }) {
  const colors = ['bg-[#8A6819]', 'bg-[#7A5963]', 'bg-[#5A5147]', 'bg-[#BCAFA0]']

  return (
    <div
      className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${colors[index % colors.length]} text-xs font-bold text-white`}
    >
      {getInitials(name)}
    </div>
  )
}

function PaymentBadge({ value }) {
  const isInsurance = value === 'convenio'

  return (
    <span
      className={`inline-flex rounded-full px-3 py-1 text-xs font-bold ${
        isInsurance ? 'bg-[#F8E1E7] text-[#7A5963]' : 'bg-[#F3E8CE] text-[#8A6819]'
      }`}
    >
      {formatPaymentType(value)}
    </span>
  )
}
